import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  forwardRef,
} from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Textarea,
  DatePicker,
  TimePicker,
  Label,
  Card,
  CardContent,
  normalizeTimeValue,
} from '@roger/ui-components';
import { updateOrder } from '../../services/api';
import type { OrderResponse } from '../../services/api/types.gen';
import { parseISOToLocalDate, formatDateToISO } from '../../utils/dateUtils';
import { getHttpStatus } from '../../utils/httpErrorUtils';
import {
  useAutoSave,
  SaveDeclinedError,
  type AutoSaveStatus,
  type SaveOutcome,
} from '../../hooks/useAutoSave';
import { useToast } from '../../hooks/useToastContext';

export interface GenericOrderFormHandle {
  validate: () => Promise<boolean>;
  flush: () => Promise<SaveOutcome>;
}

interface GenericOrderFormProps {
  visit: OrderResponse;
  // Fires after a successful autosave, carrying the freshly-persisted order so
  // the parent can refresh from the response instead of refetching. See the
  // call site in performSave for why this is non-destructive.
  onSave?: (updatedOrder: OrderResponse) => void;
  // The autosave hook lives here, but the indicator renders in
  // OrderDetailPanel's shared header (always visible) — so push status up for
  // the panel to mirror. ROG-2670.
  onSaveStatusChange?: (status: AutoSaveStatus, changeToken: number) => void;
}

// 24-hour HH:MM with optional :SS, matching pydantic's `time` parser so we
// accept values the backend returns (e.g. "09:00:00") as well as values the
// HH:MM input emits.
const ORDER_TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/;

const schema = z.object({
  order_date: z.string().min(1, 'Order date is required'),
  order_time: z
    .string()
    .optional()
    .refine(
      v => !v || ORDER_TIME_PATTERN.test(v),
      'Enter a valid time as HH:MM (24-hour)'
    ),
  order_text: z.string().min(1, 'Order text is required'),
  order_summary: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

function getExistingOrderData(
  orderData: OrderResponse['order_data']
): Record<string, unknown> {
  if (orderData && typeof orderData === 'object' && !Array.isArray(orderData)) {
    return orderData;
  }
  return {};
}

function visitToFormValues(visit: OrderResponse): FormValues {
  // Dedicated columns have priority; fall back to order_data JSONB
  const data = visit.order_data as Record<string, string> | null;
  return {
    order_date: visit.order_date || data?.order_date || '',
    order_time:
      normalizeTimeValue(visit.order_time || data?.order_time || '') || '',
    order_text: visit.order_text || data?.order_text || '',
    order_summary: visit.order_summary || data?.order_summary || '',
  };
}

/** Avoid sending `""` for optional fields — pydantic `time` rejects empty strings. */
function nonEmptyStringOrUndefined(
  value: string | undefined
): string | undefined {
  if (value == null || value.trim() === '') return undefined;
  return value;
}

function buildOrderDataPayload(
  existing: Record<string, unknown>,
  values: Partial<FormValues>
): Record<string, unknown> {
  const orderData: Record<string, unknown> = { ...existing, ...values };
  for (const key of ['order_time', 'order_summary'] as const) {
    const field = orderData[key];
    if (typeof field === 'string' && field.trim() === '') {
      delete orderData[key];
    }
  }
  return orderData;
}

export const GenericOrderForm = forwardRef<
  GenericOrderFormHandle,
  GenericOrderFormProps
>(({ visit, onSave, onSaveStatusChange }, ref) => {
  const { error: showError } = useToast();
  const {
    control,
    register,
    watch,
    trigger,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: visitToFormValues(visit),
    mode: 'onTouched',
  });
  const latestOrderDataRef = useRef<Record<string, unknown>>(
    getExistingOrderData(visit.order_data)
  );
  const orderDataLastUpdatedRef = useRef<string | null>(
    visit.order_data_last_updated ?? null
  );

  // Reset the OCC refs only when navigation changes orders or the server
  // advances the token. order_data changes are expected to advance
  // order_data_last_updated, and including the JSONB object would wipe in-flight
  // edit state on autosave responses.
  useEffect(() => {
    latestOrderDataRef.current = getExistingOrderData(visit.order_data);
    orderDataLastUpdatedRef.current = visit.order_data_last_updated ?? null;
  }, [visit.id, visit.order_data_last_updated]);

  const performSave = useCallback(
    async (values: Partial<FormValues>) => {
      const orderData = {
        ...buildOrderDataPayload(latestOrderDataRef.current, values),
        payload_kind: 'generic' as const,
      };

      const response = await updateOrder({
        path: { order_id: visit.id },
        body: {
          order_date: nonEmptyStringOrUndefined(values.order_date),
          order_text: nonEmptyStringOrUndefined(values.order_text),
          order_summary: nonEmptyStringOrUndefined(values.order_summary),
          order_time: nonEmptyStringOrUndefined(values.order_time),
          order_data: orderData,
          order_data_last_updated: orderDataLastUpdatedRef.current,
        },
      });

      if (response.error || !response.data) {
        console.error('Failed to save order', response.error);
        showError(
          getHttpStatus(response) === 409
            ? 'This order was updated elsewhere. Reload the order and try again.'
            : 'Failed to save order. Please try again.'
        );
        throw new Error('Failed to save order');
      }

      latestOrderDataRef.current = getExistingOrderData(
        response.data.order_data
      );
      orderDataLastUpdatedRef.current =
        response.data.order_data_last_updated ??
        orderDataLastUpdatedRef.current;

      // Hand the freshly-saved order to the parent so it can sync its own
      // state (header status, onVisitUpdated) without a refetch. This runs
      // only on the success path — a failed or validation-blocked save throws
      // above and never reaches here. useAutoSave's initialValue re-sync skips
      // an echo of the just-saved value, so bubbling this up cannot clobber
      // in-progress edits.
      onSave?.(response.data);
    },
    [onSave, showError, visit.id]
  );

  const {
    setValue: setAutoSaveValue,
    saveNow,
    saveStatus,
    changeToken,
  } = useAutoSave({
    initialValue: JSON.stringify(visitToFormValues(visit)),
    onSave: useCallback(
      async (jsonValues: string) => {
        // Re-run the form's zod schema when the debounce fires. Any field
        // failing validation blocks the save -- the inline errors already
        // tell the user why -- so the backend never sees garbage. Throwing
        // SaveDeclinedError (rather than returning) keeps the hook from
        // recording the unsaved value as the saved baseline and surfaces a
        // truthful "Not saved" instead of a false "Saved".
        const ok = await trigger();
        if (!ok) throw new SaveDeclinedError();
        await performSave(JSON.parse(jsonValues) as FormValues);
      },
      [trigger, performSave]
    ),
    debounceDelay: 1500,
  });

  useEffect(() => {
    const subscription = watch(formValues => {
      // Push the latest value on every keystroke so a queued debounce can't
      // fire a stale snapshot; surface validation errors immediately rather
      // than waiting for blur.
      setAutoSaveValue(JSON.stringify(formValues));
      void trigger();
    });
    return () => subscription.unsubscribe();
  }, [watch, setAutoSaveValue, trigger]);

  // Surface save state to the panel header. No readiness guard needed: the
  // onSave above throws SaveDeclinedError on a validation block, so the hook
  // reports 'blocked' ("Not saved") rather than a false "Saved" — and an
  // untouched form never triggers a save, so it simply stays idle.
  useEffect(() => {
    onSaveStatusChange?.(saveStatus, changeToken);
  }, [saveStatus, changeToken, onSaveStatusChange]);

  useImperativeHandle(
    ref,
    () => ({
      validate: () => trigger(),
      flush: () => saveNow(),
    }),
    [trigger, saveNow]
  );

  return (
    <Card>
      <CardContent className='space-y-4'>
        {/* Order Date */}
        <div className='space-y-1.5'>
          <Label>
            Order Date <span className='text-red-500'>*</span>
          </Label>
          <Controller
            name='order_date'
            control={control}
            render={({ field }) => (
              <DatePicker
                date={
                  field.value
                    ? (parseISOToLocalDate(field.value) ?? undefined)
                    : undefined
                }
                onDateChange={d => field.onChange(d ? formatDateToISO(d) : '')}
                datePickerSize='sm'
              />
            )}
          />
          {errors.order_date && (
            <p className='text-xs text-red-500'>{errors.order_date.message}</p>
          )}
        </div>

        {/* Order Time */}
        <div className='space-y-1.5'>
          <Label htmlFor='order-time'>Order Time</Label>
          <Controller
            name='order_time'
            control={control}
            render={({ field }) => (
              <TimePicker
                id='order-time'
                time={field.value || undefined}
                onTimeChange={time => field.onChange(time ?? '')}
                timePickerSize='sm'
                showClearButton
                error={Boolean(errors.order_time)}
              />
            )}
          />
          {errors.order_time && (
            <p className='text-xs text-red-500'>{errors.order_time.message}</p>
          )}
        </div>

        {/* Order Text */}
        <div className='space-y-1.5'>
          <Label>
            Order Text <span className='text-red-500'>*</span>
          </Label>
          <Textarea
            {...register('order_text')}
            placeholder='Enter order text'
            textareaSize='sm'
            rows={8}
          />
          {errors.order_text && (
            <p className='text-xs text-red-500'>{errors.order_text.message}</p>
          )}
        </div>

        {/* Order Summary */}
        <div className='space-y-1.5'>
          <Label>Order Summary</Label>
          <Textarea
            {...register('order_summary')}
            placeholder='Enter order summary'
            textareaSize='sm'
          />
        </div>
      </CardContent>
    </Card>
  );
});

GenericOrderForm.displayName = 'GenericOrderForm';
